/**
 * /marketplace — Social Pulse Marketplace (Phase 1).
 *
 * Public browsing — anyone can search + view. Posting and messaging require
 * sign-in. Everything is locality-scoped: each listing has a seller-chosen
 * radius (default 25 km) and buyers see only listings whose radius covers
 * their locality.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocality } from "@/contexts/LocalityContext";
import {
  Search, Filter, Plus, MapPin, DollarSign, Tag, ShoppingBag, Package,
  Loader2, ImageOff, Flag,
} from "lucide-react";

const SORTS = [
  { key: "recent",    label: "Most recent" },
  { key: "cheapest",  label: "Price: low → high" },
  { key: "expensive", label: "Price: high → low" },
];

export default function Marketplace() {
  const { user } = useAuth();
  const { locality } = useLocality();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [listingType, setListingType] = useState(params.get("listing_type") || "");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [onlyFree, setOnlyFree] = useState(params.get("only_free") === "1");
  const [radiusKm, setRadiusKm] = useState(parseInt(params.get("radius_km") || "50", 10));
  const [sort, setSort] = useState(params.get("sort") || "recent");

  // Load taxonomy once.
  useEffect(() => {
    http.get("/marketplace/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (locality?.lat) { p.set("lat", locality.lat); p.set("lng", locality.lng); p.set("radius_km", radiusKm); }
      if (q) p.set("q", q);
      if (category) p.set("category", category);
      if (listingType) p.set("listing_type", listingType);
      if (minPrice) p.set("min_price", minPrice);
      if (maxPrice) p.set("max_price", maxPrice);
      if (onlyFree) p.set("only_free", "true");
      p.set("sort", sort);
      p.set("limit", "60");
      const { data } = await http.get(`/marketplace?${p.toString()}`);
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Couldn't load marketplace.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    search();
    // Reflect in URL so filters are shareable.
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (listingType) p.set("listing_type", listingType);
    if (minPrice) p.set("min_price", minPrice);
    if (maxPrice) p.set("max_price", maxPrice);
    if (onlyFree) p.set("only_free", "1");
    if (radiusKm !== 50) p.set("radius_km", String(radiusKm));
    if (sort !== "recent") p.set("sort", sort);
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, listingType, onlyFree, sort, radiusKm, locality?.lat, locality?.lng]);

  const submit = (e) => { e.preventDefault(); search(); };
  const clearAll = () => {
    setQ(""); setCategory(""); setListingType(""); setMinPrice(""); setMaxPrice("");
    setOnlyFree(false); setRadiusKm(50); setSort("recent");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="marketplace-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">SOCIAL PULSE · MARKETPLACE</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Buy, sell, and swap locally.</h1>
          <p className="mt-3 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Community classifieds — no fees, no middlemen. Post an item, look for something you need,
            or give away what you no longer use. Meet neighbors in the process.
          </p>
        </div>
        {user ? (
          <Link to="/marketplace/new" data-testid="post-listing-btn" className="wofy-btn-primary shrink-0">
            <Plus className="w-4 h-4" /> Post a listing
          </Link>
        ) : (
          <Link to="/login" className="wofy-btn-primary shrink-0">
            <Plus className="w-4 h-4" /> Sign in to post
          </Link>
        )}
      </div>

      {!locality?.lat && (
        <div className="mt-6 rounded-lg border p-4 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
          <MapPin className="w-4 h-4 inline mr-1" />
          <b>Set your location</b> to see nearby listings — showing everyone's posts for now.
        </div>
      )}

      {/* Filters */}
      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input
          data-testid="mp-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search items, brands, places…"
          className="rounded-lg border px-3 py-2 text-sm outline-none lg:col-span-2"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <select
          data-testid="mp-category-select"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">All categories</option>
          {(meta?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select
          data-testid="mp-type-select"
          value={listingType}
          onChange={(e) => setListingType(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">Buy or sell</option>
          <option value="for_sale">For sale</option>
          <option value="looking_for">Looking for</option>
        </select>
        <input
          type="number" min="0"
          data-testid="mp-minprice-input"
          value={minPrice}
          onChange={(e) => setMinPrice(e.target.value)}
          placeholder="Min $"
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <input
          type="number" min="0"
          data-testid="mp-maxprice-input"
          value={maxPrice}
          onChange={(e) => setMaxPrice(e.target.value)}
          placeholder="Max $"
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <select
          data-testid="mp-radius-select"
          value={radiusKm}
          onChange={(e) => setRadiusKm(parseInt(e.target.value, 10))}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value={5}>5 km</option>
          <option value={25}>25 km</option>
          <option value={50}>50 km</option>
          <option value={200}>200 km</option>
          <option value={3000}>Country</option>
          <option value={20000}>Anywhere</option>
        </select>
        <select
          data-testid="mp-sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </form>

      <div className="mt-3 flex items-center gap-3 text-xs flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="checkbox" data-testid="mp-only-free" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} />
          Free stuff only
        </label>
        <button type="button" onClick={clearAll} className="underline">Clear filters</button>
        <div className="ml-auto">
          {loading ? <><Loader2 className="w-3 h-3 inline animate-spin" /> loading…</> : `${total} listing${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Results grid */}
      {!loading && results.length === 0 && (
        <div className="mt-10 rounded-lg border p-8 text-center text-sm" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          No listings match your filters. Be the first to post one!
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {results.map((r) => <ListingCard key={r.id} r={r} categoryLabel={meta?.categories?.find(c => c.key === r.category)?.label} />)}
      </div>
    </div>
  );
}

function ListingCard({ r, categoryLabel }) {
  const priceText = r.is_free
    ? "FREE"
    : r.price != null
      ? `$${Number(r.price).toLocaleString()}${r.accepts_offers ? " · OBO" : ""}`
      : (r.accepts_offers ? "Make an offer" : "—");
  const typeBadge = r.listing_type === "looking_for" ? "Looking for" : "For sale";
  const typeColor = r.listing_type === "looking_for" ? "#7c3aed" : "#2563eb";
  return (
    <Link
      to={`/marketplace/${r.id}`}
      data-testid={`listing-card-${r.id}`}
      className="rounded-2xl border overflow-hidden hover:shadow-md transition-shadow"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
    >
      <div className="aspect-[4/3] overflow-hidden bg-black/5">
        {r.photos?.[0] ? (
          <img src={r.photos[0]} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-30">
            <ImageOff className="w-8 h-8" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 text-xs">
          <span
            className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full"
            style={{ background: typeColor, color: "#fff" }}
          >
            {typeBadge}
          </span>
          {categoryLabel && (
            <span className="text-[10px] uppercase font-mono opacity-70">{categoryLabel}</span>
          )}
        </div>
        <div className="mt-2 text-sm font-medium line-clamp-2">{r.title}</div>
        <div className="mt-1 text-base font-display" style={{ color: r.is_free ? "#059669" : "var(--wofy-text)" }}>
          {priceText}
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] uppercase font-mono tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>
          <span className="truncate">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</span>
          <span className="ml-2 shrink-0">{new Date(r.bumped_at || r.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </Link>
  );
}
